import React, { useState, useEffect, useCallback, useRef } from 'react';
import { View, Text, ScrollView, FlatList, TouchableOpacity, RefreshControl, ActivityIndicator, Animated, Alert } from 'react-native';
import tw from 'twrnc';

import { isScreenshotMode, DEMO_LEADERBOARD, DEMO_USER_XP, DEMO_USER_ID } from '../../../../lib/screenshotMode';
import HeaderBar from '../../../components/HeaderBar';
import { DSGradientBackground } from '../../../design-system/components/DSGradientBackground';
import { DSText } from '../../../design-system/components/DSText';
import { spacing } from '../../../design-system/tokens';
import { t } from '../../../i18n/translations';
import { 
  getXPLeaderboard,
  getUserXP,
  getUserGamificationProfile,
  formatXP,
  getRankFromLevel,
  subscribeToLeaderboardUpdates,
  type XPLeaderboardEntry,
  type GamificationProfile,
  type UserXP
} from '../../../shared/services/gamificationService';
import { useLanguageStore } from '../../quiz-core/languageStore';
import { useUserProfile } from '../../quiz-core/useStore';

const gt = (key: string, language: string) => t(`gamification.${key}`, language);

const RANK_TRANSLATION_KEYS: Record<string, string> = {
  Beginner: 'beginner',
  Scholar: 'scholar',
  Teacher: 'teacher',
  Sage: 'sage',
  Master: 'master',
};

const translateRankName = (rankName: string, language: string) => {
  const key = RANK_TRANSLATION_KEYS[rankName];
  if (!key) return rankName;

  const translated = gt(key, language);
  return translated === `gamification.${key}` ? rankName : translated;
};

interface LeaderboardItemProps {
  entry: XPLeaderboardEntry;
  isCurrentUser: boolean;
  rank: number;
}

const LeaderboardItem: React.FC<LeaderboardItemProps> = ({ 
  entry, 
  isCurrentUser, 
  rank 
}) => {
  const animatedValue = useRef(new Animated.Value(0)).current;
  const { language } = useLanguageStore();
  const g = (key: string) => gt(key, language);

  useEffect(() => {
    Animated.timing(animatedValue, {
      toValue: 1,
      duration: 300 + (rank * 50), // Stagger animation
      useNativeDriver: true,
    }).start();
  }, [animatedValue, rank]);

  const getRankEmoji = (position: number) => {
    switch (position) {
      case 1: return '🥇';
      case 2: return '🥈';
      case 3: return '🥉';
      default: return `#${position}`;
    }
  };

  const getRankColor = (rankName: string) => {
    switch (rankName) {
      case 'Beginner': return 'text-green-600';
      case 'Scholar': return 'text-yellow-100';
      case 'Teacher': return 'text-yellow-100';
      case 'Sage': return 'text-yellow-600';
      case 'Master': return 'text-red-600';
      default: return 'text-yellow-100';
    }
  };

  const getLevelColor = (level: number) => {
    if (level >= 50) return 'text-red-600';
    if (level >= 20) return 'text-yellow-600';
    if (level >= 10) return 'text-yellow-100';
    if (level >= 5) return 'text-yellow-100';
    return 'text-green-600';
  };

  const getRankBadgeColor = (position: number) => {
    if (position === 1) return 'bg-[#3A2A08] border-yellow-500';
    if (position === 2) return 'bg-[#141022] border-yellow-700';
    if (position === 3) return 'bg-[#3A1F0A] border-orange-300';
    if (position <= 10) return 'bg-[#17234A] border-yellow-700';
    return 'bg-[#100C20] border-yellow-700';
  };

  const getCardStyle = () => {
    if (isCurrentUser) return 'bg-[#101A32] border-2 border-yellow-500';
    if (rank === 1) return 'bg-gradient-to-r from-yellow-50 to-yellow-100 border border-yellow-700';
    if (rank === 2) return 'bg-gradient-to-r from-gray-50 to-gray-100 border border-yellow-700';
    if (rank === 3) return 'bg-gradient-to-r from-orange-50 to-orange-100 border border-yellow-700';
    return 'bg-[#080A18] border border-yellow-700';
  };

  return (
    <Animated.View 
      style={[
        {
          opacity: animatedValue,
          transform: [{
            translateY: animatedValue.interpolate({
              inputRange: [0, 1],
              outputRange: [20, 0],
            }),
          }],
        }
      ]}
    >
      <View style={tw`${getCardStyle()} rounded-xl p-4 mb-3 shadow-sm`}>
        <View style={tw`flex-row items-center`}>
          {/* Rank Badge */}
          <View style={tw`w-14 items-center mr-4`}>
            <View style={tw`${getRankBadgeColor(rank)} rounded-full w-12 h-12 items-center justify-center border`}>
              <Text style={tw`text-lg font-bold ${rank <= 3 ? 'text-xl' : 'text-yellow-100'}`}>
                {getRankEmoji(rank)}
              </Text>
            </View>
          </View>

          {/* User Info */}
          <View style={tw`flex-1`}>
            <View style={tw`flex-row items-center justify-between mb-2`}>
              <View style={tw`flex-1`}>
                <Text style={tw`text-lg font-bold ${isCurrentUser ? 'text-yellow-50' : 'text-yellow-50'} mb-1`}>
                  {entry.username}
                  {isCurrentUser && (
                    <Text style={tw`text-sm text-yellow-100 font-normal`}> ({g('you')})</Text>
                  )}
                </Text>
                <View style={tw`flex-row items-center`}>
                  <Text style={tw`text-sm ${getLevelColor(entry.current_level)} font-semibold mr-3`}>
                    {g('level')} {entry.current_level}
                  </Text>
                  <Text style={tw`text-sm ${getRankColor(entry.rank)} font-medium`}>
                    {translateRankName(entry.rank, language)}
                  </Text>
                </View>
              </View>
              
              <View style={tw`items-end`}>
                <Text style={tw`text-xl font-bold text-orange-600 mb-1`}>
                  {formatXP(entry.total_xp)}
                </Text>
                <Text style={tw`text-xs text-yellow-100`}>XP</Text>
              </View>
            </View>

            {/* Progress Bar for Current User */}
            {isCurrentUser && entry.xp_to_next_level !== undefined && (
              <View style={tw`mt-2`}>
                <View style={tw`flex-row justify-between items-center mb-1`}>
                  <Text style={tw`text-xs text-yellow-100`}>
                    {g('next_level')}
                  </Text>
                  <Text style={tw`text-xs text-yellow-100`}>
                    {entry.xp_to_next_level} XP {g('needed')}
                  </Text>
                </View>
                <View style={tw`bg-[#2A2440] rounded-full h-2`}>
                  <View 
                    style={[
                      tw`bg-[#F6BE3A] rounded-full h-2`,
                      { 
                        width: `${Math.max(5, 100 - (entry.xp_to_next_level / (entry.xp_to_next_level + 100)) * 100)}%` 
                      }
                    ]}
                  />
                </View>
              </View>
            )}

            {/* Special Badges */}
            <View style={tw`flex-row mt-2`}>
              {rank <= 3 && (
                <View style={tw`${getRankBadgeColor(rank)} rounded-full px-3 py-1 mr-2`}>
                  <Text style={tw`text-xs font-semibold ${rank === 1 ? 'text-yellow-100' : rank === 2 ? 'text-yellow-100' : 'text-yellow-100'}`}>
                    {t('top', language)} {rank}
                  </Text>
                </View>
              )}
              {rank <= 10 && rank > 3 && (
                <View style={tw`bg-[#17234A] rounded-full px-3 py-1 mr-2`}>
                  <Text style={tw`text-xs font-semibold text-yellow-50`}>
                    {g('top_10')}
                  </Text>
                </View>
              )}
              {entry.current_level >= 20 && (
                <View style={tw`bg-[#28164A] rounded-full px-3 py-1`}>
                  <Text style={tw`text-xs font-semibold text-purple-800`}>
                    {g('expert')}
                  </Text>
                </View>
              )}
            </View>
          </View>
        </View>
      </View>
    </Animated.View>
  );
};

export const XPLeaderboardScreen: React.FC = () => {
  const { id: userId, fetchProfile } = useUserProfile();
  const { language } = useLanguageStore();
  const g = (key: string) => gt(key, language);
  const screenshotMode = isScreenshotMode();

  // Convert demo leaderboard data to XPLeaderboardEntry shape when in screenshot mode
  const demoLeaderboardEntries: XPLeaderboardEntry[] = screenshotMode
    ? DEMO_LEADERBOARD.map(e => ({
        user_id: e.user_id,
        username: e.username,
        total_xp: e.total_xp,
        current_level: e.current_level,
        rank: e.rank,
        position: e.position,
        avatar_url: e.avatar_url,
        xp_to_next_level: e.user_id === DEMO_USER_ID ? DEMO_USER_XP.xp_to_next_level : undefined,
      } as XPLeaderboardEntry))
    : [];

  const [leaderboard, setLeaderboard] = useState<XPLeaderboardEntry[]>(screenshotMode ? demoLeaderboardEntries : []);
  const [userXP, setUserXP] = useState<UserXP | null>(screenshotMode ? (DEMO_USER_XP as unknown as UserXP) : null);
  const [gamificationProfile, setGamificationProfile] = useState<GamificationProfile | null>(null);
  const [userRank, setUserRank] = useState<number | null>(screenshotMode ? 6 : null);
  const [loading, setLoading] = useState(screenshotMode ? false : true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<'all' | 'friends' | 'local'>('all');
  const [limit, setLimit] = useState(50);
  type LeaderboardFilter = 'all' | 'friends' | 'local';

  // Animation values
  const fadeAnim = useRef(new Animated.Value(screenshotMode ? 1 : 0)).current;
  const slideAnim = useRef(new Animated.Value(screenshotMode ? 0 : -50)).current;
  
  // Real-time subscription
  const subscriptionRef = useRef<{ unsubscribe: () => void } | null>(null);

  // Screenshot mode: signal readiness after first render
  useEffect(() => {
    if (screenshotMode && typeof window !== 'undefined') {
      (window as typeof window & { __screenshotReady?: string }).__screenshotReady = '04_leaderboard';
    }
  }, [screenshotMode]);

  useEffect(() => {
    if (screenshotMode) return;
    void fetchProfile();
  }, [screenshotMode, fetchProfile]);

  useEffect(() => {
    if (screenshotMode) return;
    if (userId) {
      loadLeaderboard();
      setupRealtimeSubscription();
    }
    
    return () => {
      if (subscriptionRef.current) {
        subscriptionRef.current.unsubscribe();
      }
    };
  }, [screenshotMode, userId, filter, limit]);

  const setupRealtimeSubscription = () => {
    // Clean up existing subscription
    if (subscriptionRef.current) {
      subscriptionRef.current.unsubscribe();
    }

    subscriptionRef.current = subscribeToLeaderboardUpdates({
      onLeaderboardUpdate: (newLeaderboard: XPLeaderboardEntry[]) => {
        if (!loading && !refreshing) {
          setLeaderboard(newLeaderboard);
          
          // Update user rank
          const userEntry = newLeaderboard.find((entry: XPLeaderboardEntry) => entry.user_id === userId);
          setUserRank(userEntry?.position || null);
        }
      },
      onError: (error: Error) => {
        console.error('Leaderboard subscription error:', error);
        setError('Real-time updates temporarily unavailable');
      }
    });
  };

  const loadLeaderboard = useCallback(async (silent = false) => {
    if (!userId) {
      setLoading(false);
      return;
    }

    try {
      if (!silent) {
        setLoading(true);
      }
      setError(null);
      
      const [leaderboardData, userXPData, profileData] = await Promise.all([
        getXPLeaderboard(limit),
        getUserXP(userId),
        getUserGamificationProfile(userId)
      ]);

      setLeaderboard(leaderboardData);
      setUserXP(userXPData);
      setGamificationProfile(profileData);

      // Find user's rank in leaderboard
      const userEntry = leaderboardData.find(entry => entry.user_id === userId);
      setUserRank(userEntry?.position || null);

      // Animate content in
      if (!silent) {
        Animated.parallel([
          Animated.timing(fadeAnim, {
            toValue: 1,
            duration: 500,
            useNativeDriver: true,
          }),
          Animated.timing(slideAnim, {
            toValue: 0,
            duration: 400,
            useNativeDriver: true,
          }),
        ]).start();
      }

    } catch (error) {
      console.error('Error loading leaderboard:', error);
      setError(g('leaderboard_error'));
      
      if (!silent) {
        Alert.alert(
          t('error', language),
          g('leaderboard_error'),
          [
            { text: t('ok', language) },
            { text: t('retry', language), onPress: () => loadLeaderboard() }
          ]
        );
      }
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  }, [userId, filter, limit, language, fadeAnim, slideAnim]);

  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    await loadLeaderboard();
    setRefreshing(false);
  }, [loadLeaderboard]);

  const loadMore = useCallback(() => {
    if (leaderboard.length >= limit && limit < 200) {
      setLimit(prev => prev + 50);
    }
  }, [leaderboard.length, limit]);

  const renderUserStats = () => {
    if (!userXP) return null;

    const nextRank = getRankFromLevel(userXP.current_level + 1);
    const progressPercentage = Math.max(5, 100 - (userXP.xp_to_next_level / (userXP.xp_to_next_level + 100)) * 100);
    const quizzesCompleted = gamificationProfile?.quizzes_completed ?? userXP.quizzes_completed ?? 0;
    const streakCount = gamificationProfile?.current_streak ?? userXP.current_streak ?? 0;
    const achievementsCount = gamificationProfile?.completed_achievements ?? userXP.achievements_count ?? 0;

    return (
      <Animated.View 
        style={[
          tw`mx-4 mt-4 rounded-[28px] p-6`,
          {
            backgroundColor: '#080A18',
            borderWidth: 1,
            borderColor: 'rgba(246,190,58,0.38)',
            shadowColor: '#F6BE3A',
            shadowOpacity: 0.16,
            shadowRadius: 18,
            shadowOffset: { width: 0, height: 8 },
            elevation: 3,
            opacity: fadeAnim,
            transform: [{ translateY: slideAnim }],
          }
        ]}
      >
        <View style={tw`flex-row items-center justify-between mb-3`}>
          <View style={[tw`rounded-full px-3 py-2`, { backgroundColor: 'rgba(246,190,58,0.14)', borderWidth: 1, borderColor: 'rgba(246,190,58,0.32)' }]}>
            <DSText variant="micro" uppercase color="#F6BE3A" style={{ fontWeight: '700' }}>
              {g('leaderboard')}
            </DSText>
          </View>
          <View style={[tw`rounded-full px-3 py-2`, { backgroundColor: 'rgba(111,66,193,0.24)', borderWidth: 1, borderColor: 'rgba(246,190,58,0.24)' }]}>
            <DSText variant="micro" color="#FFF7DA">
              {userRank ? `#${userRank}` : g('unranked')}
            </DSText>
          </View>
        </View>
        <View style={tw`flex-row items-center justify-between mb-4`}>
          <View style={tw`flex-1`}>
            <DSText variant="title" color="#FFF7DA" style={{ marginBottom: spacing.xs, fontSize: 20 }}>
              {g('leaderboard_hero_title')}
            </DSText>
            <DSText variant="body" color="rgba(255,248,232,0.74)">
              {g('leaderboard_hero_subtitle')}
            </DSText>
          </View>
          {userRank && userRank <= 10 && (
            <View style={tw`bg-[#3A2A08] rounded-full px-3 py-2 border border-yellow-500`}>
              <Text style={tw`text-xs font-bold text-yellow-100 tracking-wide uppercase`}>
                {g('top_10')}
              </Text>
            </View>
          )}
        </View>
        
        <View style={tw`flex-row justify-between items-start mb-6`}>
          <View style={tw`items-center flex-1 px-1`}>
            <View style={[tw`bg-[#080A18] rounded-2xl items-center justify-center mb-2 border border-yellow-700`, { width: 72, height: 72 }]}>
              <Text style={tw`${userRank ? 'text-2xl' : 'text-[11px]'} font-bold text-yellow-100 text-center px-1`}>
                {userRank || g('unranked')}
              </Text>
            </View>
            <Text style={tw`text-xs text-yellow-100 font-medium text-center`}>
              {g('rank')}
            </Text>
          </View>
          
          <View style={tw`items-center flex-1 px-1`}>
            <View style={[tw`bg-[#080A18] rounded-2xl items-center justify-center mb-2 border border-yellow-700`, { width: 72, height: 72 }]}>
              <Text style={tw`text-lg font-bold text-orange-600`}>
                {formatXP(userXP.total_xp)}
              </Text>
            </View>
            <Text style={tw`text-xs text-yellow-100 font-medium text-center`}>
              XP
            </Text>
          </View>
          
          <View style={tw`items-center flex-1 px-1`}>
            <View style={[tw`bg-[#080A18] rounded-2xl items-center justify-center mb-2 border border-yellow-700`, { width: 72, height: 72 }]}>
              <Text style={tw`text-2xl font-bold text-yellow-100`}>
                {userXP.current_level}
              </Text>
            </View>
            <Text style={tw`text-xs text-yellow-100 font-medium text-center`}>
              {g('level')}
            </Text>
          </View>
        </View>

        {/* Progress Section */}
        <View style={tw`bg-[#080A18] rounded-2xl p-4 border border-yellow-700`}>
          <View style={tw`flex-row justify-between items-center mb-3`}>
            <Text style={tw`flex-1 mr-3 text-sm font-semibold text-yellow-100`}>
              {g('progress_to_next_level')}
            </Text>
            <Text style={tw`text-sm text-yellow-100`}>
              {userXP.xp_to_next_level} XP {g('needed')}
            </Text>
          </View>
          
          <View style={tw`bg-[#2A2440] rounded-full h-3 mb-3 overflow-hidden`}>
            <Animated.View 
              style={[
                tw`bg-[#F6BE3A] rounded-full h-3`,
                { width: `${progressPercentage}%` }
              ]}
            />
          </View>
          
          <View style={tw`flex-row justify-between items-center`}>
            <Text style={tw`text-sm font-medium text-yellow-100`}>
              {translateRankName(userXP.rank, language)}
            </Text>
            <Text style={tw`text-sm text-yellow-100`}>
              {Math.round(progressPercentage)}% {g('completed').toLowerCase()}
            </Text>
            <Text style={tw`text-sm font-medium text-yellow-100`}>
              {nextRank === userXP.rank
                ? `${g('level')} ${userXP.current_level + 1}`
                : translateRankName(nextRank, language)}
            </Text>
          </View>
        </View>

        {/* Quick Stats */}
        <View style={tw`flex-row justify-between mt-4 pt-4 border-t border-yellow-700`}>
          <View style={tw`items-center flex-1 px-1`}>
            <Text style={tw`text-lg font-bold text-green-600`}>
              {quizzesCompleted}
            </Text>
            <Text style={tw`text-[11px] text-yellow-100 text-center`}>
              {g('quizzes_completed')}
            </Text>
          </View>
          <View style={tw`items-center flex-1 px-1`}>
            <Text style={tw`text-lg font-bold text-yellow-100`}>
              {streakCount}
            </Text>
            <Text style={tw`text-[11px] text-yellow-100 text-center`}>
              {g('day_streak')}
            </Text>
          </View>
          <View style={tw`items-center flex-1 px-1`}>
            <Text style={tw`text-lg font-bold text-yellow-100`}>
              {achievementsCount}
            </Text>
            <Text style={tw`text-[11px] text-yellow-100 text-center`}>
              {g('achievements')}
            </Text>
          </View>
        </View>
      </Animated.View>
    );
  };

  const renderHeader = () => (
    <>
      {renderUserStats()}

      <View style={tw`mx-4 mt-4`}>
        <ScrollView horizontal showsHorizontalScrollIndicator={false}>
          <View style={tw`flex-row`}>
            {[
              { key: 'all', label: g('all_players'), icon: '🌍' },
              { key: 'friends', label: t('leaderboard.friends', language), icon: '👥' },
              { key: 'local', label: g('local'), icon: '📍' }
            ].map((filterOption) => (
              <TouchableOpacity
                key={filterOption.key}
                style={tw`mr-3 ${filter === filterOption.key ? 'bg-[#3A2A08]' : 'bg-[#080A18]'} rounded-full px-4 py-2 border ${filter === filterOption.key ? 'border-yellow-500' : 'border-yellow-700'}`}
                onPress={() => setFilter(filterOption.key as LeaderboardFilter)}
              >
                <Text style={tw`${filter === filterOption.key ? 'text-white' : 'text-yellow-100'} font-medium text-sm`}>
                  {filterOption.icon} {filterOption.label}
                </Text>
              </TouchableOpacity>
            ))}
          </View>
        </ScrollView>
      </View>

      {leaderboard.length >= 3 && (
        <Animated.View
          style={[
            tw`bg-[#080A18] mx-4 mt-4 rounded-[28px] p-6 shadow-sm border border-yellow-700`,
            {
              opacity: fadeAnim,
              transform: [{ translateY: slideAnim }],
            }
          ]}
        >
          <Text style={tw`text-xs font-bold text-yellow-100 mb-2 tracking-widest uppercase text-center`}>
            {g('top_performers')}
          </Text>
          <Text style={tw`text-sm text-yellow-100 mb-6 text-center`}>
            {g('leaderboard_hero_subtitle')}
          </Text>

          <View style={tw`flex-row justify-center items-end mb-4`}>
            <View style={tw`items-center mx-1 flex-1`}>
              <Animated.View style={{ transform: [{ scale: fadeAnim.interpolate({ inputRange: [0, 1], outputRange: [0.8, 1] }) }] }}>
                <Text style={tw`text-4xl mb-2`}>🥈</Text>
              </Animated.View>
              <View style={tw`bg-[#141022] rounded-[20px] px-3 py-6 items-center w-full border border-yellow-700`}>
                <Text style={tw`text-[11px] font-bold text-yellow-50 text-center mb-1 min-h-[48px] leading-4 px-1`} numberOfLines={3}>
                  {leaderboard[1]?.username}
                </Text>
                <Text style={tw`text-xs text-yellow-100 font-semibold`}>
                  {formatXP(leaderboard[1]?.total_xp)}
                </Text>
                <Text style={tw`text-xs text-yellow-100`}>
                  {g('level')} {leaderboard[1]?.current_level}
                </Text>
              </View>
            </View>

            <View style={tw`items-center mx-1 flex-1`}>
              <Animated.View style={{ transform: [{ scale: fadeAnim.interpolate({ inputRange: [0, 1], outputRange: [0.8, 1.2] }) }] }}>
                <Text style={tw`text-5xl mb-2`}>🥇</Text>
              </Animated.View>
              <View style={tw`bg-[#3A2A08] rounded-[22px] px-3 py-8 items-center w-full border border-yellow-500`}>
                <Text style={tw`text-[11px] font-bold text-yellow-50 text-center mb-1 min-h-[48px] leading-4 px-1`} numberOfLines={3}>
                  {leaderboard[0]?.username}
                </Text>
                <Text style={tw`text-xs text-yellow-100 font-bold`}>
                  {formatXP(leaderboard[0]?.total_xp)}
                </Text>
                <Text style={tw`text-xs text-yellow-100`}>
                  {g('level')} {leaderboard[0]?.current_level}
                </Text>
                <View style={tw`bg-[#5A3C09] rounded-full px-2 py-1 mt-2`}>
                  <Text style={tw`text-xs font-bold text-yellow-100`}>
                    👑 {g('champion')}
                  </Text>
                </View>
              </View>
            </View>

            <View style={tw`items-center mx-1 flex-1`}>
              <Animated.View style={{ transform: [{ scale: fadeAnim.interpolate({ inputRange: [0, 1], outputRange: [0.8, 1] }) }] }}>
                <Text style={tw`text-4xl mb-2`}>🥉</Text>
              </Animated.View>
              <View style={tw`bg-[#3A1F0A] rounded-[20px] px-3 py-4 items-center w-full border border-yellow-700`}>
                <Text style={tw`text-[11px] font-bold text-yellow-50 text-center mb-1 min-h-[48px] leading-4 px-1`} numberOfLines={3}>
                  {leaderboard[2]?.username}
                </Text>
                <Text style={tw`text-xs text-yellow-100 font-semibold`}>
                  {formatXP(leaderboard[2]?.total_xp)}
                </Text>
                <Text style={tw`text-xs text-yellow-100`}>
                  {g('level')} {leaderboard[2]?.current_level}
                </Text>
              </View>
            </View>
          </View>
        </Animated.View>
      )}

      <View style={tw`flex-row justify-between items-center px-4 mt-4 mb-3`}>
        <Text style={tw`text-lg font-bold text-yellow-50`}>
          {g('full_rankings')}
        </Text>
        <TouchableOpacity
          style={tw`bg-[#080A18] rounded-full px-4 py-2 border border-yellow-700`}
          onPress={handleRefresh}
          disabled={refreshing}
        >
          <Text style={tw`text-yellow-100 font-semibold text-sm`}>
            {refreshing ? g('refreshing') : g('refresh_cta')}
          </Text>
        </TouchableOpacity>
      </View>
    </>
  );

  if (loading && leaderboard.length === 0) {
    return (
      <DSGradientBackground preset="surface">
        <HeaderBar title={g('leaderboard')} variant="simple" showProfile={false} showClose={false} />
        <View style={tw`flex-1 justify-center items-center p-4`}>
          <ActivityIndicator size="large" color="#F6BE3A" />
          <Text style={tw`text-lg text-yellow-100 mt-4 text-center`}>
            {g('loading_leaderboard')}...
          </Text>
          <Text style={tw`text-sm text-yellow-100 mt-2 text-center`}>
            {g('loading_leaderboard_desc')}
          </Text>
        </View>
      </DSGradientBackground>
    );
  }

  if (error && leaderboard.length === 0) {
    return (
      <DSGradientBackground preset="surface">
        <HeaderBar title={g('leaderboard')} variant="simple" showProfile={false} showClose={false} />
        <View style={tw`flex-1 justify-center items-center p-4`}>
          <Text style={tw`text-6xl mb-4`}>⚠️</Text>
          <Text style={tw`text-lg text-yellow-50 mb-2 text-center font-semibold`}>
            {g('leaderboard_error')}
          </Text>
          <Text style={tw`text-sm text-yellow-100 mb-6 text-center`}>
            {g('leaderboard_error_desc')}
          </Text>
          <TouchableOpacity
            style={tw`bg-[#3A2A08] rounded-lg py-3 px-6 border border-yellow-600`}
            onPress={() => loadLeaderboard()}
          >
            <Text style={tw`text-white font-semibold`}>
              {t('retry', language)}
            </Text>
          </TouchableOpacity>
        </View>
      </DSGradientBackground>
    );
  }

  return (
    <DSGradientBackground preset="surface">
      <HeaderBar title={g('leaderboard')} variant="simple" showProfile={false} showClose={false} />
      <FlatList
        data={leaderboard}
        keyExtractor={(item) => item.user_id}
        renderItem={({ item, index }) => (
          <LeaderboardItem
            entry={item}
            isCurrentUser={item.user_id === userId}
            rank={item.position || index + 1}
          />
        )}
        ListHeaderComponent={renderHeader}
        contentContainerStyle={tw`px-4 pb-6`}
        showsVerticalScrollIndicator={false}
        refreshControl={
          <RefreshControl
            refreshing={refreshing}
            onRefresh={handleRefresh}
            colors={['#F6BE3A']}
            tintColor="#F6BE3A"
            title={g('pull_to_refresh')}
          />
        }
        onEndReached={loadMore}
        onEndReachedThreshold={0.1}
        removeClippedSubviews={true}
        maxToRenderPerBatch={10}
        windowSize={10}
        initialNumToRender={20}
        getItemLayout={(data, index) => ({
          length: 120,
          offset: 120 * index,
          index,
        })}
        ListEmptyComponent={
          <View style={tw`items-center justify-center py-12`}>
            <Text style={tw`text-6xl mb-4`}>📊</Text>
            <Text style={tw`text-lg font-semibold text-yellow-100 mb-2`}>
              {g('no_rankings')}
            </Text>
            <Text style={tw`text-sm text-yellow-100 text-center px-8`}>
              {g('no_rankings_description')}
            </Text>
            <TouchableOpacity
              style={tw`bg-[#3A2A08] rounded-lg py-3 px-6 mt-4 border border-yellow-600`}
              onPress={() => loadLeaderboard()}
            >
              <Text style={tw`text-white font-semibold`}>
                {g('refresh_cta')}
              </Text>
            </TouchableOpacity>
          </View>
        }
        ListFooterComponent={
          leaderboard.length > 0 && leaderboard.length >= limit && limit < 200 ? (
            <View style={tw`items-center py-4`}>
              <TouchableOpacity
                style={tw`bg-[#141022] rounded-lg py-3 px-6`}
                onPress={loadMore}
              >
                <Text style={tw`text-yellow-100 font-semibold`}>
                  {t('load_more', language)}
                </Text>
              </TouchableOpacity>
            </View>
          ) : leaderboard.length > 0 ? (
            <View style={tw`items-center py-6`}>
              <Text style={tw`text-sm text-yellow-100`}>
                {g('end_of_leaderboard')}
              </Text>
            </View>
          ) : null
        }
      />
    </DSGradientBackground>
  );
};
